import {
  CreateEntitySummaryBundleConfigurationInput,
  UpdateEntitySummaryBundleConfigurationInput,
} from '@/types/schema';

import {
  BundleConfigurationPayload,
  BundleSaveDescriptor,
  DiffBundleConfigurationArgs,
} from '../presentationSave.types';
import { createFlatConfigDescriptor } from './flatConfigDescriptor';

const flatEntitySummaryBundleDescriptor = createFlatConfigDescriptor({
  field: 'entitySummaryConfiguration',
  read: (bundle) => bundle.entitySummaryConfiguration,
  diff: (previous, next) => {
    const update: UpdateEntitySummaryBundleConfigurationInput = {};

    if (previous.entityId !== next.entityId) update.entityID = next.entityId;
    if (previous.deathOrderId !== next.deathOrderId) {
      update.deathOrderID = next.deathOrderId;
    }
    if (previous.includeEntitySummaryPage !== next.includeEntitySummaryPage) {
      update.includeEntitySummaryPage = next.includeEntitySummaryPage;
    }
    if (
      previous.includeBeneficiariesAndDispositionsPage !==
      next.includeBeneficiariesAndDispositionsPage
    ) {
      update.includeBeneficiariesAndDispositionsPage =
        next.includeBeneficiariesAndDispositionsPage;
    }
    if (
      previous.includeDiagramAndNotesPage !== next.includeDiagramAndNotesPage
    ) {
      update.includeDiagramAndNotesPage = next.includeDiagramAndNotesPage;
    }
    if (previous.includeBalanceSheetPage !== next.includeBalanceSheetPage) {
      update.includeBalanceSheetPage = next.includeBalanceSheetPage;
    }
    if (
      previous.includeBalanceSheetExportPage !==
      next.includeBalanceSheetExportPage
    ) {
      update.includeBalanceSheetExportPage = next.includeBalanceSheetExportPage;
    }
    if (
      previous.includeEntityNotesOnSummaryPage !==
      next.includeEntityNotesOnSummaryPage
    ) {
      update.includeEntityNotesOnSummaryPage =
        next.includeEntityNotesOnSummaryPage;
    }
    if (
      previous.includeInsurancePolicySlides !==
      next.includeInsurancePolicySlides
    ) {
      update.includeInsurancePolicySlides = next.includeInsurancePolicySlides;
    }

    // deathOrderNotes belongs to the entity and is saved separately.
    return Object.keys(update).length > 0 ? update : null;
  },
  toPayload: (id, update) => ({
    updateEntitySummaryBundleConfiguration: { id, update },
  }),
});

export const entitySummaryBundleDescriptor: BundleSaveDescriptor = {
  ownedFields: flatEntitySummaryBundleDescriptor.ownedFields,
  canWrite(previous, next) {
    const before = previous.entitySummaryConfiguration;
    const after = next.entitySummaryConfiguration;
    if (!before && after) {
      return Boolean(after.entityId && after.deathOrderId);
    }
    return flatEntitySummaryBundleDescriptor.canWrite(previous, next);
  },
  diffConfiguration(args) {
    const before = args.previous.entitySummaryConfiguration;
    const after = args.next.entitySummaryConfiguration;
    if (!before && after) {
      return buildReplacementConfiguration(after, args);
    }
    return flatEntitySummaryBundleDescriptor.diffConfiguration(args);
  },
};

function buildReplacementConfiguration(
  configuration: NonNullable<
    DiffBundleConfigurationArgs['next']['entitySummaryConfiguration']
  >,
  { context }: DiffBundleConfigurationArgs
): BundleConfigurationPayload {
  const create: CreateEntitySummaryBundleConfigurationInput = {
    householdID: context.householdId,
    entityID: configuration.entityId,
    deathOrderID: configuration.deathOrderId,
    includeEntitySummaryPage: configuration.includeEntitySummaryPage,
    includeBeneficiariesAndDispositionsPage:
      configuration.includeBeneficiariesAndDispositionsPage,
    includeDiagramAndNotesPage: configuration.includeDiagramAndNotesPage,
    includeBalanceSheetPage: configuration.includeBalanceSheetPage,
    includeBalanceSheetExportPage: configuration.includeBalanceSheetExportPage,
    includeEntityNotesOnSummaryPage:
      configuration.includeEntityNotesOnSummaryPage,
    includeInsurancePolicySlides: configuration.includeInsurancePolicySlides,
  };

  return {
    update: { clearEntitySummaryBundleConfiguration: true },
    withEntitySummaryBundleConfiguration: { create },
  };
}
